import { Formik } from "formik";
import * as Yup from "yup";
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Select,
  Tooltip,
  Space,
  InputNumber,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { Calculator, Edit3, Trash2 } from "lucide-react";

const { TextArea } = Input;

// Validation schema
const validationSchema = Yup.object().shape({
  field_id: Yup.string().nullable().required("Calculation Field is required"),
  type: Yup.string().nullable().required("Field Type is required"),
  unit_id: Yup.string().nullable().required("Unit is required"),
  min_value: Yup.number()
    .nullable()
    .typeError("Must be a number")
    .required("Min value is required"),
  max_value: Yup.number()
    .nullable()
    .typeError("Must be a number")
    .required("Max value is required")
    .moreThan(Yup.ref("min_value"), "Max must be greater than Min"),
  formula: Yup.string().nullable().required("Formula is required"),
  instruction: Yup.string().nullable().required("Instruction is required"),
});

const ApiCalculationForm = ({
  onSubmit = (values) => console.log("API Calc Values:", values),
  calculation_field_list = [],
  unit_list = [],
  values = {
    field_id: null,
    type: "Number",
    unit_id: null,
    min_value: null,
    max_value: null,
    formula: "",
    instruction: null,
  },
}) => {
  return (
    <Formik
      initialValues={values}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ values, errors, touched, handleSubmit, setFieldValue }) => (
        <Form layout="vertical" onFinish={handleSubmit}>
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item
                label="Calculation Field"
                validateStatus={
                  touched.field_id && errors.field_id ? "error" : ""
                }
                help={touched.field_id && errors.field_id}
              >
                <Space.Compact style={{ width: "100%" }}>
                  <Select
                    placeholder="Select Calculation Field"
                    value={values.field_id || null}
                    onChange={(val, option) => {
                      setFieldValue("field_id", val || "");
                      if (val) {
                        setFieldValue("field", option.label || "");
                      }
                    }}
                    showSearch
                    allowClear
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={calculation_field_list?.map((item) => ({
                      value: item.calculation_field_id,
                      label: item.name,
                    }))}
                  />
                  {/* <Tooltip title="Add Calculation Field">
                    <Button icon={<PlusOutlined />} />
                  </Tooltip> */}
                </Space.Compact>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Field Type"
                validateStatus={touched.type && errors.type ? "error" : ""}
                help={touched.type && errors.type}
              >
                <Input value={values.type} disabled />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Min Value"
                validateStatus={
                  touched.min_value && errors.min_value ? "error" : ""
                }
                help={touched.min_value && errors.min_value}
              >
                <Space.Compact className="w-full">
                  <InputNumber
                    className="w-full"
                    placeholder={`Min Value`}
                    value={values.min_value}
                    onChange={(val) => setFieldValue("min_value", val)}
                  />
                </Space.Compact>
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item
                label="Max Value"
                validateStatus={
                  touched.max_value && errors.max_value ? "error" : ""
                }
                help={touched.max_value && errors.max_value}
              >
                <Space.Compact className="w-full">
                  <InputNumber
                    className="w-full"
                    placeholder={`Max value`}
                    value={values.max_value}
                    onChange={(val) => setFieldValue("max_value", val)}
                  />
                </Space.Compact>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Unit"
                validateStatus={
                  touched.unit_id && errors.unit_id ? "error" : ""
                }
                help={touched.unit_id && errors.unit_id}
              >
                <Space.Compact style={{ width: "100%" }}>
                  <Select
                    placeholder="Select unit"
                    value={values.unit_id || null}
                    onChange={(val, option) => {
                      setFieldValue("unit_id", val || "");
                      if (val) {
                        setFieldValue("unit", option.label);
                      }
                    }}
                    showSearch
                    allowClear
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={unit_list?.map((unit) => ({
                      value: unit.id,
                      label: unit.unit_name,
                    }))}
                  />
                  {/* <Tooltip title="Add Unit">
                    <Button icon={<PlusOutlined />} />
                  </Tooltip> */}
                </Space.Compact>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Formula"
                validateStatus={
                  touched.formula && errors.formula ? "error" : ""
                }
                help={touched.formula && errors.formula}
              >
                <Input
                  placeholder="Formula"
                  value={values.formula}
                  onChange={(e) => setFieldValue("formula", e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Instruction"
                validateStatus={
                  touched.instruction && errors.instruction ? "error" : ""
                }
                help={touched.instruction && errors.instruction}
              >
                <TextArea
                  rows={2}
                  placeholder="Instruction"
                  value={values.instruction}
                  onChange={(e) => setFieldValue("instruction", e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Button type="primary" htmlType="submit" block>
                Submit
              </Button>
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  );
};

export default ApiCalculationForm;

export const ApiCalculationCard = ({ item, onEdit, onDelete }) => {
  const { field, field_id, type, unit, min_value, max_value, instruction } =
    item;

  const showLabel = (label, id) => label || `${id ? "ID: " + id : "N/A"}`;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Calculator className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <p
              className="font-semibold text-gray-800 leading-tight truncate"
              title={showLabel(field, field_id)}
            >
              {showLabel(field, field_id)}
            </p>
            <p
              className="text-xs text-gray-600 truncate"
              title={`Type: ${type}`}
            >
              Type: {type}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="p-1.5 rounded-md hover:bg-blue-50 transition-colors border border-transparent hover:border-blue-200"
            onClick={() => onEdit()}
            title="Edit"
          >
            <Edit3 className="w-4 h-4 text-blue-600" />
          </button>
          <button
            type="button"
            className="p-1.5 rounded-md hover:bg-red-50 transition-colors border border-transparent hover:border-red-200"
            onClick={() => onDelete()}
            title="Delete"
          >
            <Trash2 className="w-4 h-4 text-red-500" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-2 gap-2">
        {/* Unit */}
        {unit && (
          <div className="bg-indigo-50 rounded-lg p-2">
            <p className="text-xs text-indigo-700 font-medium">Unit</p>
            <p className="text-indigo-900 text-sm truncate" title={unit}>
              {unit}
            </p>
          </div>
        )}

        {/* Range */}
        {(min_value !== null || max_value !== null) && (
          <div className="bg-orange-50 rounded-lg p-2">
            <p className="text-xs text-orange-700 font-medium">Range</p>
            <p
              className="text-orange-900 text-sm truncate"
              title={`${min_value} - ${max_value} ${unit}`}
            >
              {min_value !== null ? min_value : "N/A"} -{" "}
              {max_value !== null ? max_value : "N/A"}{" "}
              <span className="text-xs">{unit}</span>
            </p>
          </div>
        )}
      </div>

      {/* Instruction */}
      {instruction && (
        <div className="mt-3 bg-gray-50 rounded-lg p-2">
          <p className="text-xs text-gray-600 font-medium">Instruction</p>
          <p
            className="text-gray-700 italic text-sm truncate"
            title={instruction}
          >
            {instruction}
          </p>
        </div>
      )}
    </div>
  );
};
